import {Component, computed, DestroyRef, inject, input, output, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';

import {FormControl, FormGroup, Validators} from "@angular/forms";
import {BreakpointObserver} from "@angular/cdk/layout";
import {CreateTenant, TenantService} from "../../../../services/tenant/tenant.service";
import {tap} from "rxjs/operators";
import {finalize, map} from "rxjs";
import {CurrentTenantStore} from "../../../../services/tenant/current-tenant.store";
import {Router} from "@angular/router";
import {HOME_URL} from "../../../../models/home/navigation.model";
import {TranslatePipe} from "@ngx-translate/core";
import {IconButtonComponent} from 'src/app/modules/design-system/icon-button/icon-button.component';
import {MultiSelectComponent} from 'src/app/modules/design-system/multi-select/multi-select.component';
import {SelectTriggerComponent} from 'src/app/modules/design-system/multi-select/select-trigger/select-trigger.component';
import {SelectOptionComponent} from 'src/app/modules/design-system/multi-select/select-option/select-option.component';
import {TextButtonComponent} from 'src/app/modules/design-system/text-button/text-button.component';
import {TextInputComponent} from 'src/app/modules/design-system/text-input/text-input.component';

@Component({
  selector: 'app-onboarding-step-3',
  standalone: true,
	imports: [CommonModule, IconButtonComponent, MultiSelectComponent, SelectTriggerComponent, SelectOptionComponent, TextButtonComponent, TextInputComponent, TranslatePipe],
  templateUrl: './onboarding-step-3.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: '../onboarding.component.scss'
})
export class OnboardingStep3Component {
	private tenantService = inject(TenantService);
	// initialize() selects the workspace just created.
	private tenantStore = inject(CurrentTenantStore);
	private router = inject(Router);
	private destroyRef = inject(DestroyRef);

	protected readonly form: FormGroup = new FormGroup({
		'name': new FormControl(undefined, [Validators.required]),
		'size': new FormControl('xs'),
		'activity': new FormControl(undefined),
	});

	private readonly compact = toSignal(inject(BreakpointObserver).observe(['(max-width: 1280px)']).pipe(map(state => state.matches)), {initialValue: false});
	protected readonly height = computed(() => this.compact() ? 35 : 60);
	protected readonly panelClass = computed(() => this.compact() ? '' : 'big-button');
	protected readonly panelClassInput = computed(() => this.compact() ? 'little-input' : 'big-input');
	protected readonly fontSize = computed<'S' | 'L'>(() => this.compact() ? 'S' : 'L');

	readonly secondStepForm = input.required<FormGroup>();
	readonly stepCount = input.required<number>();
	readonly finalStep = input(false);
	readonly canClose = input(false);
	readonly isMobile = input.required<boolean>();

	readonly onNextClick = output<string>();

	readonly onComplete = output<string>();

	protected readonly loading = signal(false);

	submitForm() {
		if (this.form.valid) {
			const secondStepForm = this.secondStepForm();
			const request: CreateTenant = {
				name: this.form.get('name')!.value,
				organizationEmployees: this.form.get('size')?.value,
				organizationSector: this.form.get('activity')?.value,
				accountExpectations: secondStepForm.get('objectives')?.value,
				userType: secondStepForm.get('userKind')?.value,
				companyType: secondStepForm.get('workPlace')?.value,
				onboardingCompleted: this.stepCount() === 3
			};

			this.loading.set(true);
			if (!this.finalStep()) {
				this.tenantService.createTenant(request)
					.pipe(
						tap((tenantId) => this.tenantStore.initialize(tenantId)),
						finalize(() => this.loading.set(false)),
						takeUntilDestroyed(this.destroyRef))
					.subscribe(() => {
						this.onNextClick.emit('Step 4')
					});
			} else {
				this.tenantService.createTenant(request)
					.pipe(
						finalize(() => this.loading.set(false)),
						takeUntilDestroyed(this.destroyRef))
					.subscribe((tenantId) => {
						this.onComplete.emit(tenantId)
					});
			}

		}
	}

	goBackHome() {
		return this.router.navigate([HOME_URL])
	}
}
